import { useState } from 'react';
import type { Pilot } from '@edh/shared';
import {
  PLACEMENT_GAMES,
  display,
  formatRecord,
  games,
  isRanked,
  pilotEloSeries,
  pilotRecord,
  rankFor,
  rankThrough,
} from '@edh/shared';
import { MemberProfile } from './MemberProfile';
import { RankBadge } from './RankBadge';
import { RankedAvatar } from './RankedAvatar';
import { useStore } from '../lib/store';

/** Top three carry a medal. No rank at all during placement. */
const MEDAL = ['gold', 'silver', 'bronze'] as const;

/**
 * A guild member on the standings ladder, opening their profile the way a deck
 * row opens its deck.
 *
 * Safe to tap now that the profile is a read-first view: the actions that
 * change anything live at the bottom of it, behind the record and the decks,
 * rather than under the finger of someone checking whether they are winning.
 */
export function MemberRow({ pilot, rank }: { pilot: Pilot; rank?: number }) {
  const { state, lastResult } = useStore();
  const [open, setOpen] = useState(false);
  const record = pilotRecord(state.matches, pilot.id);
  const ranked = isRanked(pilot.placementGames);
  const delta = lastResult?.match.deltas.pilots[pilot.id];
  const medal = rank && rank <= 3 ? MEDAL[rank - 1] : undefined;
  const standing = rankThrough(pilotEloSeries(state.matches, pilot.id, pilot.elo));
  const title = rankFor(pilot.elo, standing.index);

  return (
    <>
    <button
      className={`row${delta !== undefined ? ' is-participant' : ''}`}
      onClick={() => setOpen(true)}
    >
      <div className="row-body">
        <div className="row-top">
          {rank !== undefined && (
            <span className={`rank${medal ? ` ${medal}` : ''}`} aria-label={`rank ${rank}`}>
              {rank}
            </span>
          )}
          <RankedAvatar pilot={pilot} size={30} />
          <span className="row-name">{pilot.name}</span>
          {ranked ? (
            <span className="row-rating">{display(pilot.elo)}</span>
          ) : (
            // Progress, not a rating. It sits where the rating goes, so it has
            // to read as something other than one.
            <span className="pip gold num">
              {pilot.placementGames}/{PLACEMENT_GAMES}
            </span>
          )}
        </div>

        {/* Spec 10 wants games played beside every rating, so a 1341 off six
            games does not read the same as a 1341 off sixty. It is wins plus
            losses and so was already there to be worked out — but a number you
            have to add up is not one anybody reads, and this is the only place
            in the app that credits turning up. */}
        <div className="row-meta">
          {ranked && (
            <>
              <RankBadge
                elo={pilot.elo}
                atIndex={standing.index}
                held={standing.held}
                size="small"
              />
              <span className="dot">·</span>
            </>
          )}
          <span className="row-games num">{games(record)}</span>
          <span>{games(record) === 1 ? 'game' : 'games'}</span>
          <span className="dot">·</span>
          <span>{formatRecord(record)}</span>
        </div>

        {/*
         * Progress through the current rank, not position in the league's
         * spread. The old bar measured the pool and said so nowhere, so it
         * got asked about every time somebody saw it. This one is explained by
         * the rank name sitting directly above it.
         */}
        <div className={`rank-bar${ranked ? '' : ' provisional'}`}>
          <span
            style={{
              width: `${(ranked ? title.progress : pilot.placementGames / PLACEMENT_GAMES) * 100}%`,
              background: ranked ? title.color : 'var(--gold)',
            }}
          />
        </div>
      </div>
    </button>

    {open && <MemberProfile pilot={pilot} onClose={() => setOpen(false)} />}
    </>
  );
}
